import { RadiobuttonCard } from '@velocity/ui'
import * as R from 'ramda'
import * as React from 'react'

import {
  useContent,
  useAppConfig,
  useQuery,
} from '@ngb-frontend/shared/context'
import {
  DRIVER_STEP_FORM_ID,
  StepFooterDisabledSync,
} from '@ngb-frontend/shared/ui'
import { formatCategory, useVehicle } from '@ngb-frontend/shared/utils'

import { useReplacementVehicleStepStyles } from './ReplacementVehicleStep.styled'

import type {
  ReplacementVehicleStepPrerequisiteData,
  ReplacementVehicleStepValues,
  StepProps,
} from '@ngb-frontend/shared/types'

type Choice = 'courtesy' | 'rental'

const ReplacementVehicleStep: React.FC<
  StepProps<
    ReplacementVehicleStepValues,
    ReplacementVehicleStepPrerequisiteData
  >
> = ({ onNextStep, onUpdateStep, stepData }) => {
  const c = useContent()
  const config = useAppConfig()
  const query = useQuery()
  const classes = useReplacementVehicleStepStyles()
  const { data: vehicle } = useVehicle(query)

  const courtesyOffered = !!config.features.driverCourtesy
  const rentalOffered = !!vehicle?.extraServices.ReplacementVehicle

  const [choice, setChoice] = React.useState<Choice | undefined>(
    stepData?.courtesyVehicle
      ? 'courtesy'
      : stepData?.replacementVehicle
      ? 'rental'
      : undefined,
  )
  const selected =
    (choice === 'courtesy' && courtesyOffered) ||
    (choice === 'rental' && rentalOffered)
      ? choice
      : undefined

  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (!selected) return

      onUpdateStep(
        selected === 'courtesy'
          ? { replacementVehicle: false, courtesyVehicle: true }
          : {
              replacementVehicle: true,
              courtesyVehicle: false,
              replacementVehicleCategory: vehicle?.replacementVehicleCategory,
            },
      )
      onNextStep()
    },
    [onNextStep, onUpdateStep, selected, vehicle?.replacementVehicleCategory],
  )

  return (
    <form
      id={DRIVER_STEP_FORM_ID}
      onSubmit={handleSubmit}
      data-e2e-component="replacement-vehicle-form"
    >
      <div
        className={classes.cards}
        role="radiogroup"
        aria-label={c('steps.replacementVehicle.title')}
      >
        {courtesyOffered && (
          <div data-e2e-component="courtesy-vehicle-button">
            <RadiobuttonCard
              name="replacementVehicleChoice"
              value="courtesy"
              title={c('steps.replacementVehicle.courtesyVehicle')}
              details={`${c('steps.replacementVehicle.free')} - ${c(
                'steps.replacementVehicle.categoryAB',
              )}`}
              checked={selected === 'courtesy'}
              onChange={() => setChoice('courtesy')}
            />
          </div>
        )}
        <div data-e2e-component="rental-vehicle-button">
          <RadiobuttonCard
            name="replacementVehicleChoice"
            value="rental"
            title={c('steps.replacementVehicle.rentalVehicle')}
            details={
              rentalOffered
                ? c('steps.replacementVehicle.category', {
                    variables: {
                      category: formatCategory(
                        vehicle?.replacementVehicleCategory || '',
                      ),
                    },
                  })
                : c('steps.replacementVehicle.noRental')
            }
            disabled={!rentalOffered}
            checked={selected === 'rental'}
            onChange={() => setChoice('rental')}
          />
        </div>
      </div>
      <StepFooterDisabledSync disabled={!selected} />
    </form>
  )
}

export const hasReplacementVehicleValues = (
  x: unknown,
): x is ReplacementVehicleStepValues =>
  R.allPass([
    R.pipe(R.prop('replacementVehicle'), R.is(Boolean)),
    R.pipe(R.prop('courtesyVehicle'), R.is(Boolean)),
  ])(x)

export default ReplacementVehicleStep
